import "./App.css";
import "./Home.scss";
import { lazy, Suspense, useLayoutEffect } from "react";
import { FeedbackProvider } from "./feedback/FeedbackProvider";
import PatientRealtimeNotifications from "./components/PatientRealtimeNotifications/PatientRealtimeNotifications";
import WebPushBootstrap from "./components/WebPushBootstrap/WebPushBootstrap";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Home from "./Pages/Home/Home";
import CategoryDetails from "./Pages/Categories/CategoryDetails";
import VerifyOtp from "./Pages/Sign/VerifyOtp/VerifyOtp";
import LanguagePage from "./Pages/LanguagePage/LanguagePage";
import CarePriceGate from "./Pages/Categories/BidSection/CarePriceUI/CarePriceGate";
import Appointment from "./Pages/Categories/BidSection/Appointment/Appointment";
import DoctorProfile from "./Pages/DoctorSignup/DoctorProfile/DoctorProfile";
import PatientRequestsPage from "./Pages/PatientRequests/PatientRequestsPage";
import CompleteProfile from "./Pages/CompleteProfile/CompleteProfile";
import AboutUs from "./Pages/AboutUs/AboutUs";
import UploadComplete from "./Pages/UploadComplete/UploadComplete";
import PendingApproval from "./Pages/PendingApproval/PendingApproval";
import ConsentPage from "./Pages/ConsentPage/ConsentPage";
import ContactSection from "./components/ContactSection/ContactSection";
import DoctorVerifyOTP from "./Pages/DoctorSignup/DoctorVerifyOTP/DoctorVerifyOTP";
import DoctorProfileComplete from "./Pages/DoctorSignup/DoctorProfileComplete/DoctorProfileComplete";
import ConfirmedPatients from "./Pages/ConfirmedPatients/ConfirmedPatients";
import DoctorNotifications from "./Pages/DoctorNotifications/DoctorNotifications";
import DoctorAppointments from "./Pages/DoctorAppointments/DoctorAppointments";
import DoctorAvailability from "./Pages/DoctorAvailability/DoctorAvailability";
import DoctorSettings from "./Pages/DoctorSettings/DoctorSettings";
import AppointmentConfirmed from "./Pages/Categories/BidSection/Appointment/AppointmentConfirmed/AppointmentConfirmed";
import RequireVerifiedDocs from "./components/RequireVerifiedDocs";
import HospitalVerifyOTP from "./Pages/HospitalFlow/HospitalVerifyOTP";
import HospitalProfileComplete from "./Pages/HospitalFlow/HospitalProfileComplete";
import HospitalConfirmedPatients from "./Pages/HospitalFlow/HospitalConfirmedPatients";
import HospitalAppointments from "./Pages/HospitalFlow/HospitalAppointments";
import HospitalNotifications from "./Pages/HospitalFlow/HospitalNotifications";
import HospitalSettings from "./Pages/HospitalFlow/HospitalSettings";
import PatientAppShell from "./Layout/PatientAppShell";
import PatientAppLayout from "./components/PatientAppLayout/PatientAppLayout";
import PatientChatsHub from "./Pages/PatientApp/PatientChatsHub";
import PatientSupportPage from "./Pages/PatientApp/PatientSupportPage";
import PatientMenuPage from "./Pages/PatientApp/PatientMenuPage";
import PatientProfileMenuPage from "./Pages/PatientApp/PatientProfileMenuPage";
import PatientProfileLegalDocPage from "./Pages/PatientApp/PatientProfileLegalDocPage";
import PatientProfileAppVersionPage from "./Pages/PatientApp/PatientProfileAppVersionPage";
import PatientProfileSettingsPage from "./Pages/PatientApp/PatientProfileSettingsPage";
import PatientDocuments from "./Pages/PatientDocuments/PatientDocuments";
import PatientNotificationsPage from "./Pages/PatientApp/PatientNotificationsPage";
import { tokenManager } from "./Api/tokenManager";
import AuthGateway from "./Pages/AuthGateway/AuthGateway";
import RequestNew from "./Pages/RequestFlow/RequestNew";
import RequestFlowLayout from "./Pages/RequestFlow/RequestFlowLayout";
import RequestUploadEntry from "./Pages/RequestFlow/RequestUploadEntry";
import RequestCategory from "./Pages/RequestFlow/RequestCategory";
import RequestBudget from "./Pages/RequestFlow/RequestBudget";
import RequestDetails from "./Pages/RequestFlow/RequestDetails";
import RequestReviewPage from "./Pages/RequestFlow/RequestReviewPage";
import RequestBiddingRedirect from "./Pages/RequestFlow/RequestBiddingRedirect";
import RequestAppointmentRedirect from "./Pages/RequestFlow/RequestAppointmentRedirect";

const BudgetSharedSuccess = lazy(() => import("./Pages/BudgetSharedSuccess/BudgetSharedSuccess"));
const HealBidDashboard = lazy(() => import("./HealBidDashboard/HealBidDashboard"));
const PatientMatchRequestDetail = lazy(() =>
  import("./Pages/PatientMatchRequestDetail/PatientMatchRequestDetail")
);
const DoctorDashboard = lazy(() => import("./Pages/DoctorDashboard/DoctorDashboard"));
const HospitalDashboard = lazy(() => import("./Pages/HospitalFlow/HospitalDashboard"));
const ProviderChats = lazy(() => import("./Pages/ProviderChats/ProviderChats"));

const getAuthenticatedRole = () => {
  const patientToken = tokenManager.getTokenForRole("patient");
  const doctorToken = tokenManager.getTokenForRole("doctor");
  const hospitalToken = tokenManager.getTokenForRole("hospital");
  const activeProviderRole = tokenManager.getActiveProviderRole();

  // Website default preference: if patient is logged in, open patient flow first.
  if (patientToken) return "patient";
  if (activeProviderRole === "hospital" && hospitalToken) return "hospital";
  if (activeProviderRole === "doctor" && doctorToken) return "doctor";
  if (hospitalToken) return "hospital";
  if (doctorToken) return "doctor";
  return null;
};

const getDefaultRouteForRole = (role) => {
  if (role === "hospital") return "/hospital-dashboard";
  if (role === "doctor") return "/doctor-dashboard";
  if (role === "patient") return "/HealBidDashboard";
  return "/";
};

function RootEntry() {
  const role = getAuthenticatedRole();
  if (role) {
    return <Navigate to={getDefaultRouteForRole(role)} replace />;
  }
  return <Home />;
}

function PublicAuthRoute({ children }) {
  const role = getAuthenticatedRole();
  if (role) {
    return <Navigate to={getDefaultRouteForRole(role)} replace />;
  }
  return children;
}

function PatientPrivateRoute({ children }) {
  const patientToken = tokenManager.getTokenForRole("patient");
  if (!patientToken) return <Navigate to="/get-started" replace />;
  return children;
}

/** Reset window scroll on client-side navigation (SPAs keep scroll position by default). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <FeedbackProvider theme="patient">
      <Router>
        <ScrollToTop />
        <PatientRealtimeNotifications />
        <WebPushBootstrap />
        <Suspense fallback={<div style={{ padding: 16 }}>Loading...</div>}>
        <Routes>
          <Route
            path="/Login"
            element={<Navigate to="/get-started?provider=patient&mode=login" replace />}
          />
          <Route
            path="/Signup"
            element={<Navigate to="/get-started?provider=patient&mode=signup" replace />}
          ></Route>
          <Route path="/verify-otp" element={<VerifyOtp />} />

          <Route
            path="/doctor-login"
            element={<Navigate to="/get-started?provider=doctor&mode=login" replace />}
          />
          <Route
            path="/DoctorSignup"
            element={<Navigate to="/get-started?provider=doctor&mode=signup" replace />}
          ></Route>
          <Route path="/DoctorVerifyOTP" element={<DoctorVerifyOTP />}></Route>
          <Route
            path="/doctor-profile-complete"
            element={<DoctorProfileComplete />}
          />

          <Route path="/doctor-dashboard" element={<DoctorDashboard />} />
          <Route path="/doctor-bids" element={<DoctorDashboard />} />
          <Route path="/confirmed-patients" element={<ConfirmedPatients />} />
          {/* Sidebar links for doctors point to /doctor-confirmed-patients */}
          <Route
            path="/doctor-confirmed-patients"
            element={<ConfirmedPatients />}
          />
          <Route path="/doctor-appointments" element={<DoctorAppointments />} />
          <Route path="/doctor-availability" element={<DoctorAvailability />} />
          <Route
            path="/doctor-notifications"
            element={<DoctorNotifications />}
          />
          <Route path="/doctor-chats" element={<ProviderChats />} />
          <Route path="/doctor-settings" element={<DoctorSettings />} />

          {/* Hospital flow (doctor UX clone with hospital-scoped wrappers) */}
          <Route
            path="/hospital-login"
            element={<Navigate to="/get-started?provider=hospital&mode=login" replace />}
          />
          <Route
            path="/HospitalSignup"
            element={<Navigate to="/get-started?provider=hospital&mode=signup" replace />}
          ></Route>
          <Route
            path="/HospitalVerifyOTP"
            element={<HospitalVerifyOTP />}
          ></Route>
          <Route
            path="/hospital-profile-complete"
            element={<HospitalProfileComplete />}
          />
          <Route path="/hospital-dashboard" element={<HospitalDashboard />} />
          <Route path="/hospital-bids" element={<HospitalDashboard />} />
          <Route
            path="/hospital-confirmed-patients"
            element={<HospitalConfirmedPatients />}
          />
          <Route
            path="/hospital-appointments"
            element={<HospitalAppointments />}
          />
          <Route
            path="/hospital-notifications"
            element={<HospitalNotifications />}
          />
          <Route path="/hospital-chats" element={<ProviderChats />} />
          <Route path="/hospital-settings" element={<HospitalSettings />} />

          {/* Marketing + onboarding: normal website layout (no mobile app shell) */}
          <Route path="/" element={<RootEntry />} />
          <Route path="/dashboard" element={<Navigate to="/HealBidDashboard" replace />} />
          <Route path="/get-started" element={<PublicAuthRoute><AuthGateway /></PublicAuthRoute>} />
          <Route path="/home" element={<Home />} />
          <Route path="/category/:id" element={<CategoryDetails />} />
          <Route path="/LanguagePage" element={<LanguagePage />} />
          <Route path="/doctor-profile" element={<DoctorProfile />} />

          {/* Legacy / renamed patient routes → hub */}
          <Route path="/PatientBidsDashboard" element={<Navigate to="/HealBidDashboard" replace />} />
          <Route path="/PatientDoctorChat" element={<Navigate to="/patient-chats" replace />} />
          <Route path="/upload-complete" element={<UploadComplete />} />

          {/* Logged-in patient hub: PatientAppShell (bottom nav when profile complete — see PatientAppShell) */}
          <Route element={<PatientAppShell />}>
            <Route
              path="/care-price"
              element={
                <PatientPrivateRoute>
                  <RequireVerifiedDocs>
                    {/* No appBarEndSlot — bell removed from care-price chrome */}
                    <PatientAppLayout backLabel="Home">
                      <CarePriceGate />
                    </PatientAppLayout>
                  </RequireVerifiedDocs>
                </PatientPrivateRoute>
              }
            />
            <Route path="/HealBidDashboard" element={<HealBidDashboard />} />
            <Route
              path="/budget-shared-success"
              element={
                <PatientPrivateRoute>
                  <BudgetSharedSuccess />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-requests"
              element={
                <PatientPrivateRoute>
                  <PatientRequestsPage />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/request/new"
              element={
                <PatientPrivateRoute>
                  <RequestNew />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/request/:id"
              element={
                <PatientPrivateRoute>
                  <RequestFlowLayout />
                </PatientPrivateRoute>
              }
            >
              <Route path="upload" element={<RequestUploadEntry />} />
              <Route path="category" element={<RequestCategory />} />
              <Route path="budget" element={<RequestBudget />} />
              <Route path="details" element={<RequestDetails />} />
              <Route path="review" element={<RequestReviewPage />} />
              <Route path="bidding" element={<RequestBiddingRedirect />} />
              <Route path="bids" element={<RequestBiddingRedirect />} />
              <Route path="appointment" element={<RequestAppointmentRedirect />} />
            </Route>
            <Route
              path="/patient-match-request/:matchRequestId"
              element={
                <PatientPrivateRoute>
                  <PatientMatchRequestDetail />
                </PatientPrivateRoute>
              }
            />
            <Route path="/patient-chats" element={<PatientChatsHub />} />
            <Route
              path="/patient-support"
              element={
                <PatientPrivateRoute>
                  <PatientSupportPage />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-menu"
              element={
                <PatientPrivateRoute>
                  <PatientMenuPage />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile/details"
              element={
                <PatientPrivateRoute>
                  <CompleteProfile appVariant />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile"
              element={
                <PatientPrivateRoute>
                  <PatientProfileMenuPage />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile/terms"
              element={
                <PatientPrivateRoute>
                  <PatientProfileLegalDocPage kind="terms" />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile/policy"
              element={
                <PatientPrivateRoute>
                  <PatientProfileLegalDocPage kind="privacy" />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile/cookies"
              element={
                <PatientPrivateRoute>
                  <PatientProfileLegalDocPage kind="cookies" />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile/legal"
              element={
                <PatientPrivateRoute>
                  <Navigate to="/patient-profile" replace />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile/app-version"
              element={
                <PatientPrivateRoute>
                  <PatientProfileAppVersionPage />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-profile/settings"
              element={
                <PatientPrivateRoute>
                  <PatientProfileSettingsPage />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-documents"
              element={
                <PatientPrivateRoute>
                  <PatientDocuments />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/patient-notifications"
              element={
                <PatientPrivateRoute>
                  <PatientNotificationsPage />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/SurgicalCategoris"
              element={
                <PatientPrivateRoute>
                  <Navigate to="/request/new" replace />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/pending-approval"
              element={
                <PatientPrivateRoute>
                  <PendingApproval />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/CompleteProfile"
              element={
                <PatientPrivateRoute>
                  <Navigate to="/HealBidDashboard" replace />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/appointment"
              element={
                <PatientPrivateRoute>
                  <Appointment />
                </PatientPrivateRoute>
              }
            />
            <Route
              path="/appointment-confirmed"
              element={
                <PatientPrivateRoute>
                  <AppointmentConfirmed />
                </PatientPrivateRoute>
              }
            />
            <Route path="/AboutUs" element={<AboutUs />} />
            <Route path="/contact" element={<ContactSection />} />
            <Route path="/ConsentPage" element={<ConsentPage />} />
          </Route>
        </Routes>
        </Suspense>
      </Router>
      </FeedbackProvider>
    </div>
  );
}
export default App;
